import dayjs from "dayjs";
import { GroupedPoint } from "../../types/functions";
import { Interval, TimeUnit } from "./types";

/* ------------ TYPES ------------ */

interface DataPoint {
  [key: string]: unknown;
}

interface GroupByArgs {
  data: DataPoint[];
  interval: Interval;
  keys?: string[];
  indexBy?: string;
  factor?: number;
  method?: "sum" | "average";
  nullIsZero?: boolean;
}

interface GroupByStringArgs {
  data: DataPoint[];
  keys?: string[];
  indexBy: string;
}

/* ------------ CONSTANTES ------------ */
const UNIT_SECONDS: Record<TimeUnit, number> = {
  m: 60,
  minute: 60,
  minutes: 60,
  h: 3600,
  hour: 3600,
  hours: 3600,
  d: 86400,
  day: 86400,
  days: 86400,
  w: 604800,
  week: 604800,
  weeks: 604800,
  M: 0,
  month: 0,
  months: 0,
  millisecond: 1 / 1000,
  milliseconds: 1 / 1000,
  second: 1,
  seconds: 1,
  year: 31536000,
  years: 31536000,
  D: 86400, // Added for compatibility with dayjs.ManipulateType
  y: 31536000, // Added for compatibility with dayjs.ManipulateType
  s: 1, // Added for compatibility with dayjs.ManipulateType
  ms: 1 / 1000, // Added for compatibility with dayjs.ManipulateType
};

/* ------------ FORMATADOR ------------ */
function formatLabelByUnit(date: dayjs.Dayjs, unit: TimeUnit): string {
  const formats: Record<string, string> = {
    minute: "HH:mm",
    minutes: "HH:mm",
    m: "HH:mm",

    hour: "YYYY/MM/DD HH",
    hours: "YYYY/MM/DD HH",
    h: "YYYY/MM/DD HH",

    day: "DD/MM/YYYY",
    days: "DD/MM/YYYY",
    d: "DD/MM/YYYY",
    week: "DD/MM/YYYY",
    weeks: "DD/MM/YYYY",
    w: "DD/MM/YYYY",

    month: "MM/YYYY",
    months: "MM/YYYY",
    M: "MM/YYYY",
  };

  return date.format(formats[unit] || "YYYY-MM-DD HH:mm:ss");
}

/* ------------ IMPLEMENTAÇÃO ------------ */

// function groupByInterval({
//   data,
//   interval,
//   keys = [],
//   indexBy,
//   factor = 1,
// }: GroupByArgs): GroupedPoint[] {
//   if (!data.length) return [];

//   /* 1. Extrai quantidade e unidade (ex.: “15m” → 15 / “m”) */
//   const [, num = "1", u] = interval.match(/^(\d+)?\s*(\D+)$/) || [];
//   const amount = Number(num);
//   const unit = u as TimeUnit;

//   if (!(unit in UNIT_SECONDS)) {
//     throw new Error(`Unidade inválida: "${unit}"`);
//   }

//   /* 2. Para mês usamos arredondamento especial (startOf("month"))  */
//   const secsPerUnit = UNIT_SECONDS[unit];
//   const roundFn: ((ts: number) => number) | undefined = secsPerUnit
//     ? undefined
//     : (ts: number) =>
//         dayjs(ts * 1000) // seg→ms
//           .startOf("month")
//           .add(amount, "month")
//           .subtract(1, "millisecond")
//           .startOf("day") // mantém estabilidade em fusos
//           .unix();

//   /* 3. Agrupa em Map< bucketEpoch, { key1: soma, key2: soma … } > */
//   const buckets = new Map<number, Record<string, number>>();

//   for (const item of data) {
//     const tsRaw = item[indexBy || ""];
//     if (tsRaw == null) continue;

//     const ts =
//       typeof tsRaw === "number"
//         ? Math.floor(tsRaw)
//         : Math.floor(dayjs(String(tsRaw)).unix());

//     const bucketKey = roundFn
//       ? roundFn(ts)
//       : Math.floor(ts / (amount * secsPerUnit)) * (amount * secsPerUnit);

//     const current = buckets.get(bucketKey) || {};
//     for (const k of keys) {
//       current[k] = (current[k] ?? 0) + Number(item[k] ?? 0);
//     }
//     buckets.set(bucketKey, current);
//   }

//   /* 4. Gera array ordenado com rótulo humano; aplica factor só  */
//   const results: { sortKey: number; data: GroupedPoint }[] = [];

//   buckets.forEach((sums, clock) => {
//     const entry: GroupedPoint = {
//       [indexBy || ""]: formatLabelByUnit(dayjs(clock * 1000), unit),
//     };

//     for (const k of keys) {
//       entry[k] = (sums[k] ?? 0) / factor;
//     }

//     results.push({ sortKey: clock, data: entry });
//   });

//   return results.sort((a, b) => a.sortKey - b.sortKey).map((r) => r.data);
// }

function groupByInterval({
  data,
  interval,
  keys = [],
  indexBy,
  factor = 1,
  method = "sum",
}: GroupByArgs): GroupedPoint[] {
  if (!data.length) return [];

  const [, num = "1", u] = interval.match(/^(\d+)?\s*(\D+)$/) || [];
  const amount = Number(num);
  const unit = u as TimeUnit;

  if (!(unit in UNIT_SECONDS)) {
    throw new Error(`Unidade inválida: "${unit}"`);
  }

  const secsPerUnit = UNIT_SECONDS[unit];
  const roundFn: ((ts: number) => number) | undefined = secsPerUnit
    ? undefined
    : (ts: number) =>
        dayjs(ts * 1000)
          .startOf("month")
          .add(amount, "month")
          .subtract(1, "millisecond")
          .startOf("day")
          .unix();

  const buckets = new Map<
    number,
    Record<string, number> & { length: number }
  >();

  for (const item of data) {
    const tsRaw = item[indexBy || ""];
    if (tsRaw == null) continue;

    const ts =
      typeof tsRaw === "number"
        ? Math.floor(tsRaw)
        : Math.floor(dayjs(String(tsRaw)).unix());

    const bucketKey = roundFn
      ? roundFn(ts)
      : Math.floor(ts / (amount * secsPerUnit)) * (amount * secsPerUnit);

    const current = buckets.get(bucketKey) || { length: 0 };

    for (const k of keys) {
      if (item[k]) {
        const lengthKey = `length${k}`;
        current[lengthKey] = (current[lengthKey] ?? 0) + 1;
        current[k] = (current[k] ?? 0) + Number(item[k] ?? 0);
      }
    }
    buckets.set(bucketKey, current);
  }

  const results: { sortKey: number; data: GroupedPoint }[] = [];

  buckets.forEach((sums, clock) => {
    const entry: GroupedPoint = {
      [indexBy || ""]: formatLabelByUnit(dayjs(clock * 1000), unit),
    };

    for (const k of keys) {
      const rawValue = sums[k] ?? 0;
      entry[k] =
        method === "average"
          ? rawValue / (sums[`length${k}`] || 1) / factor
          : rawValue / factor;
    }

    results.push({ sortKey: clock, data: entry });
  });

  return results.sort((a, b) => a.sortKey - b.sortKey).map((r) => r.data);
}

function groupByString({
  data,
  keys = [],
  indexBy,
}: GroupByStringArgs): GroupedPoint[] {
  if (!data.length) return [];

  const buckets = new Map<string, Record<string, number>>();

  for (const item of data) {
    const groupKey = String(item[indexBy] ?? "");
    if (!groupKey) continue;

    const current = buckets.get(groupKey) || {};

    for (const k of keys) {
      const value = Number(item[k] ?? 0);
      current[k] = (current[k] ?? 0) + value;
    }

    buckets.set(groupKey, current);
  }

  const results: GroupedPoint[] = [];
  buckets.forEach((sums, groupKey) => {
    const entry: GroupedPoint = { [indexBy]: groupKey };

    for (const k of keys) {
      entry[k] = sums[k] ?? 0;
    }

    results.push(entry);
  });

  return results.sort((a, b) =>
    String(a[indexBy]).localeCompare(String(b[indexBy]))
  );
}

export { groupByInterval, groupByString, formatLabelByUnit };
